/**
 * SF-164: waterfall/modifier pure logic ported out of the deleted `components/council/tabs/
 * ResourceFlowsTab.tsx`, feeding both `MobileEconomyShell`/`DesktopEconomyShell`'s Flow Breakdown
 * sections and Active Modifiers table. `buildGoldRows` also moves here from
 * `ResourceFlowsTab.tsx` (it lived there originally, alongside its sibling row-builders
 * `buildFoodRows`/`buildRunesRows`, which already lived in this file's predecessor).
 *
 * `buildModifiers`/`Modifier` are restructured, not ported verbatim (v1.3 Critical Issues #6/#7):
 * the four value fields carry explicit `{text, negative}` direction instead of a string a
 * sign-sniffing classifier would guess wrong for Storms/Greed/Inspire Army. See each push site's
 * inline rationale below.
 *
 * Pre-existing, currently-live formula divergences from the real server formulas are ported
 * forward unchanged (Phase 3 decision, documented not fixed — see `buildFoodRows`/`buildRunesRows`
 * doc comments below): this spec's scope is port-and-merge, not formula correctness.
 */
import type { Province } from '../types/province';
import { formatNumber } from './format';
import { calculateGoldFlow } from './councilCalculations';
import {
  MONEY_PER_EMPLOYED,
  MONEY_PER_UNEMPLOYED,
  MONEY_PER_BANK,
  WAGE_PER_SOLDIER,
  WAGE_PER_SPECIALIST,
  WAGE_PER_ELITE,
  WAGE_PER_THIEF,
  HONOUR_INCOME_MOD,
  FERTILE_LANDS_PRODUCTION_MOD,
  DROUGHT_PRODUCTION_MOD,
  MONARCH_FOOD_PRODUCTION_MOD,
} from '../constants/modifierLookup';

// ─── Helpers ────────────────────────────────────────────────────────────────

export function signed(value: number, unit: string): string {
  const prefix = value >= 0 ? '+' : '';
  return `${prefix}${formatNumber(Math.round(value))} ${unit}`;
}

export function modPct(mod: number): string {
  if (mod === 1.0) return '—';
  const pctVal = (mod - 1) * 100;
  return `${pctVal >= 0 ? '+' : ''}${pctVal.toFixed(0)}%`;
}

export type SourceType = 'base' | 'building' | 'science' | 'spell' | 'honor' | 'personality' | 'monarch';

export const SOURCE_COLORS: Record<SourceType, string> = {
  base: '#6a7a8a',
  building: '#d4b85f',
  science: '#6a9e8e',
  spell: '#9a7ab8',
  honor: '#c4a44a',
  personality: '#8a94a8',
  monarch: '#c45a4a',
};

/** Rendered once at `DesktopEconomyShell`'s root — NOT once per `FlowSection` instance (v1.3
 *  fix; three `FlowSection`s + the Active Modifiers table all share the `.flow-row` class). */
export const ROW_HOVER_STYLE = `
  .flow-row:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

// ─── Waterfall Row Types ────────────────────────────────────────────────────

export interface FlowRow {
  label: string;
  value: number;
  unit: string;
  source: SourceType;
  calculation?: string;
  isSubtotal?: boolean;
  isTotal?: boolean;
  indent?: boolean;
  dimmed?: boolean;
}

// ─── Flow Calculations ──────────────────────────────────────────────────────

export function buildGoldRows(p: Province): { rows: FlowRow[]; net: number } {
  const flow = calculateGoldFlow(p);
  const banks = p.territory.buildings.BANK || 0;

  const rows: FlowRow[] = [
    // Income
    { label: 'Employed peasants', value: flow.income.employed, unit: 'gc', source: 'base', calculation: `${formatNumber(p.economy.employment.employed)} × ${MONEY_PER_EMPLOYED}` },
    { label: 'Unemployed peasants', value: flow.income.unemployed, unit: 'gc', source: 'base', calculation: `${formatNumber(p.economy.employment.unemployed)} × ${MONEY_PER_UNEMPLOYED}` },
    { label: 'Tax bonus', value: flow.income.taxBonus, unit: 'gc', source: 'base', calculation: `${p.economy.taxRate}% of base`, dimmed: flow.income.taxBonus === 0 },
    { label: 'Bank bonus', value: flow.income.bankBonus, unit: 'gc', source: 'building', calculation: `${banks} banks × ${MONEY_PER_BANK * 100}%`, dimmed: banks === 0 },
  ];

  // Income modifiers
  if (flow.income.modifiers.honour !== 1.0) {
    rows.push({ label: 'Honor title', value: 0, unit: modPct(flow.income.modifiers.honour), source: 'honor', indent: true });
  }
  if (flow.income.modifiers.alchemy !== 1.0) {
    rows.push({ label: 'Alchemy science', value: 0, unit: modPct(flow.income.modifiers.alchemy), source: 'science', indent: true });
  }
  if (flow.income.modifiers.plague !== 1.0) {
    rows.push({ label: 'Plague', value: 0, unit: modPct(flow.income.modifiers.plague), source: 'spell', indent: true });
  }
  if (flow.income.modifiers.riot !== 1.0) {
    rows.push({ label: 'Riots', value: 0, unit: modPct(flow.income.modifiers.riot), source: 'spell', indent: true });
  }

  rows.push({ label: 'Total Income', value: flow.income.total, unit: 'gc', source: 'base', isSubtotal: true });

  // Expenses
  rows.push({ label: 'Soldier wages', value: -flow.expenses.soldiers, unit: 'gc', source: 'base', calculation: `${formatNumber(p.population.soldiers)} × ${WAGE_PER_SOLDIER}`, dimmed: p.population.soldiers === 0 });
  rows.push({ label: 'Specialist wages', value: -flow.expenses.specialists, unit: 'gc', source: 'base', calculation: `${formatNumber(p.population.offenseSpecialists + p.population.defenseSpecialists)} × ${WAGE_PER_SPECIALIST}`, dimmed: flow.expenses.specialists === 0 });
  rows.push({ label: 'Elite wages', value: -flow.expenses.elites, unit: 'gc', source: 'base', calculation: `${formatNumber(p.population.elites)} × ${WAGE_PER_ELITE}`, dimmed: p.population.elites === 0 });
  rows.push({ label: 'Thief wages', value: -flow.expenses.thieves, unit: 'gc', source: 'base', calculation: `${formatNumber(p.population.thieves)} × ${WAGE_PER_THIEF}`, dimmed: p.population.thieves === 0 });

  // Wage modifiers
  if (flow.expenses.modifiers.inspireArmy !== 1.0) {
    rows.push({ label: 'Inspire Army', value: 0, unit: modPct(flow.expenses.modifiers.inspireArmy), source: 'spell', indent: true });
  }
  if (flow.expenses.modifiers.greed !== 1.0) {
    rows.push({ label: 'Greed', value: 0, unit: modPct(flow.expenses.modifiers.greed), source: 'spell', indent: true });
  }

  if (flow.expenses.draft > 0) {
    rows.push({ label: 'Draft costs', value: -flow.expenses.draft, unit: 'gc', source: 'base' });
  }
  if (flow.expenses.science > 0) {
    rows.push({ label: 'Science investment', value: -flow.expenses.science, unit: 'gc', source: 'science' });
  }

  rows.push({ label: 'Total Expenses', value: -flow.expenses.total, unit: 'gc', source: 'base', isSubtotal: true });
  rows.push({ label: 'Net Gold', value: flow.net, unit: 'gc/day', source: 'base', isTotal: true });

  return { rows, net: flow.net };
}

/**
 * Known, currently-live divergence from the server (Phase 3 decision, documented not fixed):
 * consumption is hardcoded to `total * 0.25`, ignoring the per-race consumption modifier the
 * server applies (e.g. Dwarf 2.0×, Undead 0.0×). Ported forward unchanged — fixing formula
 * correctness for food production is out of this spec's port-and-merge scope.
 */
export function buildFoodRows(p: Province): { rows: FlowRow[]; net: number } {
  const farms = p.territory.buildings.FARM || 0;
  const be = p.economy.buildingEfficiency;
  const barren = p.territory.totals.barren;

  const farmProduction = farms * 70 * be;
  const barrenProduction = barren * 2;
  const baseProduction = farmProduction + barrenProduction;

  // Modifiers
  const hasFertileLands = p.magic.activeEffects.some(e => e.effectType === 'FERTILE_LANDS');
  const hasDrought = p.magic.activeEffects.some(e => e.effectType === 'DROUGHT');
  const isShepherd = p.metadata.personality === 'SHEPHERD';
  const hasFoodScience = (p.sciences.books.FOOD || 0) > 0;
  // `effectPercentage` is already a fraction (e.g. 0.12 for +12%), not points — confirmed against
  // `ProvinceDtoMapper.java`'s `effectPct = scienceSystem.computeModifier(...) - 1.0`. A pre-
  // existing, currently-live bug (ported verbatim from ResourceFlowsTab.tsx, caught in Phase 4
  // rule-compliance review) divided this by 100 a second time, silently near-zeroing the bonus —
  // fixed here rather than carried forward, per this project's pre-launch convention.
  const foodScienceMod = hasFoodScience ? 1 + (p.sciences.progress.FOOD?.effectPercentage ?? 0) : 1.0;

  let productionMod = 1.0;
  if (hasFertileLands) productionMod *= FERTILE_LANDS_PRODUCTION_MOD;
  if (hasDrought && !isShepherd) productionMod *= DROUGHT_PRODUCTION_MOD;
  if (hasFoodScience) productionMod *= foodScienceMod;
  if (p.metadata.isMonarch) productionMod *= MONARCH_FOOD_PRODUCTION_MOD;

  let totalProduction = baseProduction * productionMod;

  // Shepherd additive bonus
  const shepherdBonus = isShepherd ? p.territory.totals.acres * 4.0 : 0;
  totalProduction += shepherdBonus;

  // Consumption & decay
  const consumption = p.population.total * 0.25;
  const hasVermin = p.magic.activeEffects.some(e => e.effectType === 'VERMIN');
  const decayRate = hasVermin ? 0.045 : 0.01;
  const decay = p.resources.food * decayRate;

  const net = totalProduction - consumption - decay;

  const rows: FlowRow[] = [
    { label: 'Farm production', value: farmProduction, unit: 'bushels', source: 'building', calculation: `${farms} farms × 70 × ${be.toFixed(2)} BE` },
    { label: 'Barren land', value: barrenProduction, unit: 'bushels', source: 'base', calculation: `${formatNumber(barren)} × 2` },
  ];

  if (hasFoodScience) {
    rows.push({ label: 'Food science', value: 0, unit: modPct(foodScienceMod), source: 'science', indent: true });
  }
  if (hasFertileLands) {
    rows.push({ label: 'Fertile Lands', value: 0, unit: `+${((FERTILE_LANDS_PRODUCTION_MOD - 1) * 100).toFixed(0)}%`, source: 'spell', indent: true });
  }
  if (hasDrought) {
    rows.push({ label: 'Drought', value: 0, unit: isShepherd ? 'immune' : `${((DROUGHT_PRODUCTION_MOD - 1) * 100).toFixed(0)}%`, source: 'spell', indent: true });
  }
  if (p.metadata.isMonarch) {
    rows.push({ label: 'Monarch bonus', value: 0, unit: `+${((MONARCH_FOOD_PRODUCTION_MOD - 1) * 100).toFixed(0)}%`, source: 'monarch', indent: true });
  }
  if (shepherdBonus > 0) {
    rows.push({ label: 'Shepherd bonus', value: shepherdBonus, unit: 'bushels', source: 'personality', calculation: `${formatNumber(p.territory.totals.acres)} acres × 4.0` });
  }

  rows.push({ label: 'Total Production', value: totalProduction, unit: 'bushels', source: 'base', isSubtotal: true });
  rows.push({ label: 'Population consumption', value: -consumption, unit: 'bushels', source: 'base', calculation: `${formatNumber(p.population.total)} pop × 0.25` });
  rows.push({ label: 'Food decay', value: -decay, unit: 'bushels', source: hasVermin ? 'spell' : 'base', calculation: hasVermin ? '4.5% (Vermin)' : '1% natural' });
  rows.push({ label: 'Net Food', value: net, unit: 'bushels/day', source: 'base', isTotal: true });

  return { rows, net };
}

/**
 * Known, currently-live divergence from the server (Phase 3 decision, documented not fixed): no
 * Monarch +10% rune bonus, even though `buildModifiers()`'s own Monarch entry doesn't list one
 * either (internally consistent, just incomplete). Ported forward unchanged.
 */
export function buildRunesRows(p: Province): { rows: FlowRow[]; net: number } {
  const towers = p.territory.buildings.TOWER || 0;
  const be = p.economy.buildingEfficiency;
  const production = towers * 12 * be;
  const decay = p.resources.runes * 0.01;
  const net = production - decay;

  const rows: FlowRow[] = [
    { label: 'Tower production', value: production, unit: 'runes', source: 'building', calculation: `${towers} towers × 12 × ${be.toFixed(2)} BE` },
    { label: 'Rune decay', value: -decay, unit: 'runes', source: 'base', calculation: '1% natural' },
    { label: 'Net Runes', value: net, unit: 'runes/day', source: 'base', isTotal: true },
  ];

  return { rows, net };
}

// ─── Cross-Cutting Modifiers ────────────────────────────────────────────────

/**
 * SF-164 (v1.3 fix, Critical Issues #6/#7): each value field carries explicit direction instead
 * of a `string | null` a classifier would have to guess at — the previously-planned
 * `isNegativeModValue()` string classifier is dropped entirely, not retrofitted. `ModCell`
 * (desktop, `FlowSection.tsx`) and the mobile chip renderer both consume this shape directly, so
 * there's no second classifier for the two platforms to disagree about.
 */
export interface ModifierValue {
  text: string;
  negative: boolean;
}

export interface Modifier {
  name: string;
  source: SourceType;
  gold: ModifierValue | null;
  food: ModifierValue | null;
  runes: ModifierValue | null;
  other: ModifierValue | null;
}

export function buildModifiers(p: Province): Modifier[] {
  const mods: Modifier[] = [];
  const effects = new Set(p.magic.activeEffects.map(e => e.effectType));
  const banks = p.territory.buildings.BANK || 0;
  const farms = p.territory.buildings.FARM || 0;
  const towers = p.territory.buildings.TOWER || 0;

  // Buildings — all beneficial.
  if (banks > 0) mods.push({ name: `Banks (${banks})`, source: 'building', gold: { text: `+${(banks * MONEY_PER_BANK * 100).toFixed(0)}% income`, negative: false }, food: null, runes: null, other: null });
  if (farms > 0) mods.push({ name: `Farms (${farms})`, source: 'building', gold: null, food: { text: `+${formatNumber(Math.round(farms * 70 * p.economy.buildingEfficiency))} bushels`, negative: false }, runes: null, other: null });
  if (towers > 0) mods.push({ name: `Towers (${towers})`, source: 'building', gold: null, food: null, runes: { text: `+${formatNumber(Math.round(towers * 12 * p.economy.buildingEfficiency))} runes`, negative: false }, other: null });

  // Science — both beneficial.
  if ((p.sciences.books.ALCHEMY || 0) > 0) {
    mods.push({ name: 'Alchemy', source: 'science', gold: { text: '+40% income', negative: false }, food: null, runes: null, other: null });
  }
  if ((p.sciences.books.FOOD || 0) > 0) {
    // effectPercentage is a fraction (e.g. 0.12) — ×100 for the percent-string display, same fix
    // as foodScienceMod above.
    const effectPct = (p.sciences.progress.FOOD?.effectPercentage ?? 0) * 100;
    mods.push({ name: 'Food science', source: 'science', gold: null, food: { text: `+${effectPct.toFixed(1)}% production`, negative: false }, runes: null, other: null });
  }

  // Honor — beneficial (income bonus for title).
  const honourMod = HONOUR_INCOME_MOD[p.metadata.title] || 1.0;
  if (honourMod !== 1.0) {
    mods.push({ name: `Honor (${p.metadata.title.charAt(0) + p.metadata.title.slice(1).toLowerCase()})`, source: 'honor', gold: { text: `+${((honourMod - 1) * 100).toFixed(0)}% income`, negative: false }, food: null, runes: null, other: null });
  }

  // Monarch — beneficial.
  if (p.metadata.isMonarch) {
    mods.push({ name: 'Monarch', source: 'monarch', gold: null, food: { text: '+10% production', negative: false }, runes: null, other: null });
  }

  // Spells
  if (effects.has('FERTILE_LANDS')) {
    mods.push({ name: 'Fertile Lands', source: 'spell', gold: null, food: { text: '+25% production', negative: false }, runes: null, other: null });
  }
  if (effects.has('DROUGHT')) {
    // Real fix (v1.3, Critical Issues #6/#7): checks isShepherd the same way buildFoodRows
    // already does, so a Shepherd province sees "immune"/green in both the Flow Breakdown and
    // Active Modifiers sections instead of the two contradicting each other.
    const isShepherd = p.metadata.personality === 'SHEPHERD';
    mods.push({
      name: 'Drought',
      source: 'spell',
      gold: null,
      food: isShepherd ? { text: 'immune', negative: false } : { text: '-25% production', negative: true },
      runes: null,
      other: { text: '-15% draft speed', negative: true },
    });
  }
  if (effects.has('PLAGUE')) {
    mods.push({ name: 'Plague', source: 'spell', gold: { text: '-15% income', negative: true }, food: null, runes: null, other: { text: '-15% defense', negative: true } });
  }
  if (effects.has('RIOT')) {
    mods.push({ name: 'Riots', source: 'spell', gold: { text: '-20% income', negative: true }, food: null, runes: null, other: null });
  }
  if (effects.has('VERMIN')) {
    mods.push({ name: 'Vermin', source: 'spell', gold: null, food: { text: '4.5× decay', negative: true }, runes: null, other: null });
  }
  if (effects.has('INSPIRE_ARMY')) {
    // Both fields are benefits (lower wages, faster training) despite their leading '-' sign —
    // a sign-based classifier would have gotten this backwards (v1.3 fix).
    mods.push({ name: 'Inspire Army', source: 'spell', gold: { text: '-15% wages', negative: false }, food: null, runes: null, other: { text: '-20% training time', negative: false } });
  }
  if (effects.has('GREED')) {
    // Both fields are cost increases despite their leading '+' sign — a sign-based classifier
    // would have gotten this backwards (v1.3 fix).
    mods.push({ name: 'Greed', source: 'spell', gold: { text: '+25% wages', negative: true }, food: null, runes: null, other: { text: '+25% draft cost', negative: true } });
  }
  if (effects.has('STORMS')) {
    // No sign at all — a sign-based classifier would have defaulted this to green (v1.3 fix).
    mods.push({ name: 'Storms', source: 'spell', gold: null, food: null, runes: null, other: { text: '1.5% pop death/day', negative: true } });
  }

  // Personality — beneficial.
  if (p.metadata.personality === 'SHEPHERD') {
    mods.push({ name: 'Shepherd', source: 'personality', gold: null, food: { text: `+${formatNumber(p.territory.totals.acres * 4)} bushels`, negative: false }, runes: null, other: null });
  }

  return mods;
}
